import type { EventType, NotificationTriggerStatus } from '@/libs/interfaces'

// The automations UI exposes status as a single on/off switch; the backend
// stores it as an enum. Switching off pauses the trigger (never archives it).
export const toTriggerStatus = (isActive: boolean): NotificationTriggerStatus =>
  isActive ? 'Active' : 'Paused'

// i18n key for each event's display label — shared by the automations UI
// (NotificationTriggerRow, StoreOverrideSheet) and the template "type" tag
// dropdown (TemplateForm), so both speak the same vocabulary.
export const EVENT_LABEL_KEYS: Record<EventType, string> = {
  'order.created': 'communication.automations.eventLabel.orderCreated',
  'order.statusChanged': 'communication.automations.eventLabel.orderStatusChanged',
  'order.finished': 'communication.automations.eventLabel.orderFinished',
  'customer.created': 'communication.automations.eventLabel.customerCreated',
  'customer.updated': 'communication.automations.eventLabel.customerUpdated',
  'vehicle.created': 'communication.automations.eventLabel.vehicleCreated',
  'bill.created': 'communication.automations.eventLabel.billCreated',
  'bill.updated': 'communication.automations.eventLabel.billUpdated',
  'bill.sent': 'communication.automations.eventLabel.billSent',
  'bill.reminder': 'communication.automations.eventLabel.billReminder',
  'employee.created': 'communication.automations.eventLabel.employeeCreated',
  'employee.updated': 'communication.automations.eventLabel.employeeUpdated',
  'employee.welcome': 'communication.automations.eventLabel.employeeWelcome',
  'leave.applied': 'communication.automations.eventLabel.leaveApplied',
  'leave.approved': 'communication.automations.eventLabel.leaveApproved',
  'timesheet.submitted': 'communication.automations.eventLabel.timesheetSubmitted',
  'invite.created': 'communication.automations.eventLabel.inviteCreated',
  'invite.updated': 'communication.automations.eventLabel.inviteUpdated',
  'booking.created': 'communication.automations.eventLabel.bookingCreated',
  'booking.updated': 'communication.automations.eventLabel.bookingUpdated',
  'rentalBooking.created': 'communication.automations.eventLabel.rentalBookingCreated',
  'rentalBooking.updated': 'communication.automations.eventLabel.rentalBookingUpdated',
}

export interface EventCategory {
  key: string
  labelKey: string
  events: EventType[]
}

// Groups the trigger vocabulary into the domain sections triggers logically
// belong to, so a template's "type" and an automation's "action" can both be
// picked from a categorized list (Orders / Customers / Vehicles / Invoicing /
// Employees / Invites / Scheduling / Rental Booking) instead of one flat dropdown.
export const EVENT_CATEGORIES: EventCategory[] = [
  {
    key: 'orders',
    labelKey: 'communication.automations.category.orders',
    events: ['order.created', 'order.statusChanged', 'order.finished'],
  },
  {
    key: 'customers',
    labelKey: 'communication.automations.category.customers',
    events: ['customer.created', 'customer.updated'],
  },
  {
    key: 'vehicles',
    labelKey: 'communication.automations.category.vehicles',
    events: ['vehicle.created'],
  },
  {
    key: 'invoicing',
    labelKey: 'communication.automations.category.invoicing',
    events: ['bill.created', 'bill.updated', 'bill.sent', 'bill.reminder'],
  },
  {
    key: 'employees',
    labelKey: 'communication.automations.category.employees',
    events: [
      'employee.created',
      'employee.updated',
      'employee.welcome',
      'leave.applied',
      'leave.approved',
      'timesheet.submitted',
    ],
  },
  {
    key: 'invites',
    labelKey: 'communication.automations.category.invites',
    events: ['invite.created', 'invite.updated'],
  },
  {
    key: 'scheduling',
    labelKey: 'communication.automations.category.scheduling',
    events: ['booking.created', 'booking.updated'],
  },
  {
    key: 'rentalBooking',
    labelKey: 'communication.automations.category.rentalBooking',
    events: ['rentalBooking.created', 'rentalBooking.updated'],
  },
]

// Reverse lookup for a double-dropdown (category, then event within it): given an
// event, which category key it lives under. Used to pre-select the category
// dropdown when editing something that already has an event type set.
export function findCategoryKeyForEvent(eventType?: EventType): string | undefined {
  return EVENT_CATEGORIES.find((category) => eventType && category.events.includes(eventType))?.key
}

// Only these events are actually wired to fire on the backend today (an
// emitDomainEvent(...) call site exists for each — see domainEvents.ts /
// notificationEventListener.ts and the routes that call it). The automations UI
// (org-level rows, store overrides) offers only these, so a real user can't
// configure an automation that silently never fires. 'order.statusChanged' and
// 'invite.updated' are the two EventType values with no call site yet — they
// exist for template tagging ahead of that wiring — see EventType's doc
// comment in libs/interfaces/notificationTriggers.ts.
export const FIREABLE_EVENT_TYPES: EventType[] = [
  'order.created',
  'order.finished',
  'customer.created',
  'customer.updated',
  'vehicle.created',
  'bill.created',
  'bill.updated',
  'bill.sent',
  'bill.reminder',
  'employee.created',
  'employee.updated',
  'employee.welcome',
  'leave.applied',
  'leave.approved',
  'timesheet.submitted',
  'invite.created',
  'booking.created',
  'booking.updated',
  'rentalBooking.created',
  'rentalBooking.updated',
]
